/**
 * OmegaDecompositionPage — /omega/decomposition (S48 Step 4a).
 *
 * Founder-only (wrapped in <FounderGuard> by the route). English only,
 * mobile-responsive from 375px. Mirrors the S47 OmegaMonitorPage design system:
 * cards, green/yellow/red indicators, a 5-field human explanation, apiFetch +
 * useQuery + cn.
 *
 * Shows the Deterministic Decomposition Engine's results: the 8 Holosynthics
 * primitive kinds as cards (count + 5-field human text), plus a per-adapter
 * breakdown (code.rust vs lang.universal vs other) with counts and share.
 *
 * Data: GET /api/v1/admin/decomposition/summary (W2, Step 2c):
 *   { totals_by_kind: {kind: count}, by_adapter: [{adapter, kind_counts: {kind: count}}],
 *     results, sample_size, generated_at }
 * NOTE: totals_by_kind and kind_counts are JSON OBJECTS (serde BTreeMap), not arrays.
 * The 5-field text is UI-derived: WHAT/WHY are static per kind; HOW MANY is the
 * count; CONFIDENCE/ACTION are derived from the count. Loading / empty / error
 * are all handled — the page never 404s on empty.
 */

import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/api/http';
import { cn } from '@/utils/cn';

// ── Wire types (W2's endpoint contract) ──────────────────────────────────────

interface KindCount {
  kind: string;
  count: number;
}

interface AdapterBreakdown {
  adapter: string;
  // Wire format is a JSON object: { kindName: count }.
  kind_counts: Record<string, number>;
}

interface DecompositionSummary {
  // Wire format is a JSON object: { kindName: count } (serde BTreeMap), not an array.
  totals_by_kind: Record<string, number>;
  by_adapter: AdapterBreakdown[];
  results?: number;
  sample_size?: number;
  generated_at?: string;
}

// POST /admin/decomposition/run result (LANGUAGE stream over atoms.db).
interface RunResult {
  language?: { inputs_processed: number; aborted: boolean };
  code?: { inputs: number; aborted: boolean };
}

// The 8 Holosynthics primitive kinds, in canonical order.
const HOLOSYNTHICS_KINDS = [
  'Boolean',
  'Number',
  'Symbol',
  'Identity',
  'Space',
  'Time',
  'Relation',
  'Entropy',
] as const;

// ── Per-kind human descriptions (the static WHAT + WHY) ───────────────────────

const KIND_META: Record<string, { what: string; why: string }> = {
  Boolean: {
    what: 'Atoms that express a true/false condition or a yes/no decision.',
    why: 'They capture the rules and gates in OMEGA’s knowledge — the conditions that decide what happens next.',
  },
  Number: {
    what: 'Atoms that carry a quantity, measure, or amount.',
    why: 'They are the measurable facts — counts, prices, thresholds — OMEGA reasons over numerically.',
  },
  Symbol: {
    what: 'Atoms that name a token, label, or sign.',
    why: 'They are the vocabulary — the words and codes everything else refers to.',
  },
  Identity: {
    what: 'Atoms that identify a distinct entity (a who or a what).',
    why: 'They anchor knowledge to specific things, so OMEGA can tell one entity from another.',
  },
  Space: {
    what: 'Atoms that describe place, location, or spatial structure.',
    why: 'They situate knowledge in the world — where something is, or how its parts are arranged.',
  },
  Time: {
    what: 'Atoms that describe when something happens, or its order and duration.',
    why: 'They give knowledge a timeline — sequence, recency, and change over time.',
  },
  Relation: {
    what: 'Atoms that connect two or more things (a link, cause, or dependency).',
    why: 'They are the structure between facts — how things influence or depend on each other.',
  },
  Entropy: {
    what: 'Atoms that capture uncertainty, randomness, or disorder.',
    why: 'They mark what is unknown or noisy, so OMEGA knows the limits of its own confidence.',
  },
};

function metaFor(kind: string) {
  return (
    KIND_META[kind] ?? {
      what: 'A Holosynthics primitive kind of atom.',
      why: 'Tracked so OMEGA can see how its knowledge decomposes into primitive meaning.',
    }
  );
}

// ── Assessment: indicator + derived CONFIDENCE / ACTION from the count ────────

type Status = 'empty' | 'sparse' | 'healthy';

function assess(count: number): {
  status: Status;
  confidence: 'none' | 'low' | 'medium' | 'high';
  action: string;
} {
  if (count <= 0) {
    return {
      status: 'empty',
      confidence: 'none',
      action: 'No atoms of this kind yet — decompose more sources to populate it.',
    };
  }
  if (count < 100) {
    return {
      status: 'sparse',
      confidence: 'low',
      action: 'Sparse coverage — ingest more sources so reliable patterns can form.',
    };
  }
  if (count < 1000) {
    return {
      status: 'sparse',
      confidence: 'medium',
      action: 'Building coverage — usable now; keep decomposing to strengthen it.',
    };
  }
  return {
    status: 'healthy',
    confidence: 'high',
    action: 'Healthy coverage — reliable for reasoning over this kind.',
  };
}

const STATUS_STYLE: Record<Status, { dot: string; chip: string; label: string }> = {
  healthy: {
    dot: 'bg-success',
    chip: 'border-success/40 bg-success/10 text-success',
    label: 'Healthy',
  },
  sparse: {
    dot: 'bg-amber-500',
    chip: 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    label: 'Building',
  },
  empty: {
    dot: 'bg-ink-subtle',
    chip: 'border-border bg-surface text-ink-subtle',
    label: 'Empty',
  },
};

function fmt(n: number): string {
  return Math.round(n).toLocaleString();
}

/** Friendly label for an adapter id (code.rust / lang.universal / other). */
function adapterLabel(adapter: string): string {
  switch (adapter) {
    case 'code.rust':
      return 'Code (Rust)';
    case 'lang.universal':
      return 'Language (Universal)';
    case 'other':
      return 'Other';
    default:
      return adapter;
  }
}

// ── Field row: a labelled line of human text ──────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="text-sm leading-snug text-ink-muted">
      <span className="font-semibold text-ink">{label}: </span>
      {children}
    </p>
  );
}

// ── Kind card ─────────────────────────────────────────────────────────────────

function KindCard({ kind, count }: KindCount) {
  const meta = metaFor(kind);
  const { status, confidence, action } = assess(count);
  const style = STATUS_STYLE[status];

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-canvas p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className={cn('h-2 w-2 shrink-0 rounded-full', style.dot)} aria-hidden />
        <h3 className="text-base font-semibold text-ink">{kind}</h3>
        <span className="font-mono text-sm tabular-nums text-ink">{fmt(count)}</span>
        <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-medium', style.chip)}>
          {style.label}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <Field label="What">{meta.what}</Field>
        <Field label="Why">{meta.why}</Field>
        <Field label="How many">
          {fmt(count)} atom{count === 1 ? '' : 's'} decomposed into this kind.
        </Field>
        <Field label="Confidence">
          <span className="capitalize">{confidence}</span>
        </Field>
        <Field label="Action">{action}</Field>
      </div>
    </div>
  );
}

// ── Per-adapter breakdown ─────────────────────────────────────────────────────

function AdapterBreakdownView({ adapters }: { adapters: AdapterBreakdown[] }) {
  const rows = adapters.map((a) => ({
    adapter: a.adapter,
    total: Object.values(a.kind_counts ?? {}).reduce((acc, c) => acc + c, 0),
  }));
  const grand = rows.reduce((acc, r) => acc + r.total, 0);

  if (rows.length === 0) {
    return <p className="text-sm text-ink-muted">No per-adapter data yet.</p>;
  }

  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => {
        const pct = grand > 0 ? Math.round((r.total / grand) * 100) : 0;
        return (
          <div key={r.adapter} className="flex items-center gap-3">
            <span className="w-32 shrink-0 truncate text-sm text-ink">{adapterLabel(r.adapter)}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface">
              <div className="h-full rounded-full bg-spark/60" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-24 shrink-0 text-right font-mono text-[11px] tabular-nums text-ink-muted">
              {fmt(r.total)} · {pct}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function OmegaDecompositionPage() {
  const qc = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: ['omega', 'decomposition', 'summary'],
    queryFn: () => apiFetch<DecompositionSummary>('/admin/decomposition/summary'),
    refetchInterval: 30_000,
    staleTime: 20_000,
    retry: false,
  });

  // Run Now — triggers the converged-only decomposition pass (LANGUAGE streams
  // atoms.db). apiFetch carries the founder JWT automatically (FounderGuard route),
  // so no console is needed. The pass can be long; refresh the summary on success.
  const runNow = useMutation({
    mutationFn: () =>
      apiFetch<RunResult>('/admin/decomposition/run?language=true', { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['omega', 'decomposition', 'summary'] });
    },
  });

  // Merge onto the canonical 8 kinds so each always renders, in order.
  // totals_by_kind is an object { kindName: count }; the API serializes AtomKind
  // as snake_case (serde rename_all), so keys are lowercase ("boolean", …) —
  // look them up case-insensitively against the canonical display names.
  const byKind = new Map<string, number>(
    Object.entries(data?.totals_by_kind ?? {}).map(([k, v]) => [k.toLowerCase(), v]),
  );
  const kinds: KindCount[] = HOLOSYNTHICS_KINDS.map((kind) => ({
    kind,
    count: byKind.get(kind.toLowerCase()) ?? 0,
  }));
  const totalAtoms = kinds.reduce((acc, k) => acc + k.count, 0);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-6">
      <header className="flex flex-col gap-1">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Decomposition</h1>
          <button
            type="button"
            onClick={() => runNow.mutate()}
            disabled={runNow.isPending}
            className="min-h-[40px] rounded-md border border-spark bg-spark/10 px-4 text-sm font-semibold text-spark transition-colors hover:bg-spark/20 disabled:opacity-60"
          >
            {runNow.isPending ? 'Running…' : 'Run Now'}
          </button>
        </div>
        <p className="text-sm text-ink-muted">
          Deterministic Decomposition Engine — atoms grouped by the 8 Holosynthics primitive kinds.
        </p>
        {runNow.isPending && (
          <p className="text-[11px] text-ink-subtle">
            Decomposing the corpus — this can take a while on a large corpus. The summary refreshes
            when it finishes.
          </p>
        )}
        {runNow.isError && (
          <p className="text-[11px] text-red-400">
            Run failed or timed out — check your founder session. A long run may still be processing
            in the background; refresh shortly.
          </p>
        )}
        {runNow.isSuccess && (
          <p className="text-[11px] text-emerald-400">
            Run complete — {fmt(runNow.data?.language?.inputs_processed ?? 0)} inputs decomposed
            {runNow.data?.language?.aborted ? ' (stopped early at the volume ceiling)' : ''}.
          </p>
        )}
        {!isPending && !isError && (
          <p className="text-[11px] text-ink-subtle">
            {fmt(totalAtoms)} atoms
            {data?.generated_at &&
              ` · updated ${new Date(data.generated_at).toLocaleString([], {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}`}
          </p>
        )}
      </header>

      {isPending && <p className="text-sm text-ink-muted">Loading decomposition…</p>}

      {isError && (
        <div className="rounded-xl border border-dashed border-border bg-surface p-6 text-center">
          <p className="text-sm text-ink-muted">
            No decomposition summary available yet. Run the decomposition engine, then refresh.
          </p>
        </div>
      )}

      {!isPending && !isError && (
        <>
          <section className="flex flex-col gap-3">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-subtle">
              By adapter
            </h2>
            <div className="rounded-lg border border-border bg-canvas p-4">
              <AdapterBreakdownView adapters={data?.by_adapter ?? []} />
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="font-mono text-xs font-semibold uppercase tracking-wider text-ink-subtle">
              By Holosynthics kind
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {kinds.map((k) => (
                <KindCard key={k.kind} kind={k.kind} count={k.count} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export default OmegaDecompositionPage;
